// src/pages/TermsOfService.jsx - NEVXOAULIO VENTURES TERMS OF SERVICE
import React from 'react';
import { FileText, AlertCircle, Users, Zap, DollarSign, Scale } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            <h1 className="text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Agreement to Terms */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>

          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            By accessing and using this website and services provided by Nevxoaulio Ventures ("Company", "we", "our", "us"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you are prohibited from using or accessing this service.
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
            We reserve the right to update, change, or replace any part of these Terms of Service by posting updates on our website. It is your responsibility to check this page periodically for changes. Your continued use of or access to the website following the posting of any changes constitutes acceptance of those changes.
          </p>
        </section>

        {/* Use License */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">2. Use License</h2>

          <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Permission is granted to temporarily download one copy of the materials (information or software) on Nevxoaulio Ventures' website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
              <li>Modify or copy the materials</li>
              <li>Use the materials for any commercial purpose or for any public display</li>
              <li>Attempt to decompile or reverse engineer any software contained on the website</li>
              <li>Remove any copyright or other proprietary notations from the materials</li>
              <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
              <li>Use the materials for any illegal purpose or in violation of any laws or regulations</li>
              <li>Attempt to gain unauthorized access to any portion of the website</li>
            </ul>
          </div>
        </section>

        {/* Disclaimer of Warranties */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-red-600" />
            3. Disclaimer of Warranties
          </h2>

          <div className="bg-red-50 dark:bg-gray-800 p-6 rounded-lg border-l-4 border-red-600">
            <p className="text-gray-700 dark:text-gray-300 font-semibold mb-4">
              THE MATERIALS ON NEVXOAULIO VENTURES' WEBSITE ARE PROVIDED ON AN 'AS IS' BASIS. NEVXOAULIO VENTURES MAKES NO WARRANTIES, EXPRESSED OR IMPLIED, AND HEREBY DISCLAIMS AND NEGATES ALL OTHER WARRANTIES INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT OF INTELLECTUAL PROPERTY OR OTHER VIOLATION OF RIGHTS.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              We do not warrant that the materials on its website are accurate, complete, or current. We may make changes to the materials contained on its website at any time without notice.
            </p>
          </div>
        </section>

        {/* Limitations of Liability */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">4. Limitations of Liability</h2>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            In no event shall Nevxoaulio Ventures or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Nevxoaulio Ventures' website, even if we or an authorized representative has been notified orally or in writing of the possibility of such damage.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Some jurisdictions do not allow limitations on implied warranties or limitations of liability for incidental or consequential damages, so these limitations may not apply to you.
          </p>
        </section>

        {/* Accuracy of Materials */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">5. Accuracy of Materials</h2>

          <p className="text-gray-700 dark:text-gray-300">
            The materials appearing on Nevxoaulio Ventures' website could include technical, typographical, or photographic errors. Nevxoaulio Ventures does not warrant that any of the materials on its website are accurate, complete, or current. We may make changes to the materials contained on its website at any time without notice. However, we do not make any commitment to update the materials.
          </p>
        </section>

        {/* Materials and Links */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">6. Links</h2>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Nevxoaulio Ventures has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Nevxoaulio Ventures of the site. Use of any such linked website is at the user's own risk.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            We reserve the right to remove links to external websites at any time and without notice.
          </p>
        </section>

        {/* Modifications */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">7. Modifications</h2>

          <p className="text-gray-700 dark:text-gray-300">
            Nevxoaulio Ventures may revise these Terms of Service for its website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these Terms of Service.
          </p>
        </section>

        {/* Governing Law */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Scale className="w-6 h-6 text-blue-600" />
            8. Governing Law & Jurisdiction
          </h2>

          <p className="text-gray-700 dark:text-gray-300">
            These Terms and Conditions and any separate agreements we may enter into to update, maintain, or provide service to you in relation to your use of the website shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
          </p>
          <p className="text-gray-700 dark:text-gray-300 mt-4">
            All disputes, claims, and causes of action arising out of or relating to these Terms of Service shall be governed by the exclusive jurisdiction of the courts located in Bangalore, Karnataka, India.
          </p>
        </section>

        {/* Intellectual Property Rights */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">9. Intellectual Property Rights</h2>

          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Ownership:</span> All materials, including but not limited to text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, are the property of Nevxoaulio Ventures or its content suppliers and are protected by international copyright laws.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Restrictions:</span> You may not reproduce, republish, modify, translate, or distribute any content from this website without our prior written consent.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Trademarks:</span> All trademarks, service marks, and trade names are the property of Nevxoaulio Ventures or their respective owners and are protected by applicable laws.
            </p>
          </div>
        </section>

        {/* User Accounts */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            10. User Accounts
          </h2>

          <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-lg space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Account Responsibility</p>
              <p className="text-gray-700 dark:text-gray-300">
                If you create an account on our website, you are responsible for maintaining the confidentiality of your account information and password and for restricting access to your account.
              </p>
            </div>

            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Account Activity</p>
              <p className="text-gray-700 dark:text-gray-300">
                You agree to accept responsibility for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </div>

            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Account Termination</p>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to suspend or terminate your account and access to our services at any time for violation of these Terms of Service or for any other reason at our discretion.
              </p>
            </div>
          </div>
        </section>

        {/* Prohibited Activities */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">11. Prohibited Activities</h2>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            You agree not to engage in any of the following prohibited activities:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            <li>Harassing or causing distress or inconvenience to any person</li>
            <li>Obscene, offensive, or abusive language or behavior</li>
            <li>Intentional disruption of normal website operations</li>
            <li>Transmission of obscene, offensive, or violent content</li>
            <li>Spam, chain letters, or other unsolicited materials</li>
            <li>Hacking, unauthorized access, or security breaches</li>
            <li>Malware, viruses, or other malicious code</li>
            <li>Attempting to gain unauthorized access to our systems</li>
            <li>Interfering with other users' use and enjoyment of the website</li>
            <li>Any illegal activity or violation of applicable laws</li>
          </ul>
        </section>

        {/* Payment Terms */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            12. Payment Terms
          </h2>

          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Pricing:</span> Prices for our services are subject to change without notice. We will make reasonable efforts to notify you of material price changes.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Payment:</span> Payment must be made according to the terms specified during the purchase process. We accept major credit cards, bank transfers, and other payment methods.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Refunds:</span> Refund policies are specified in our service agreements. Not all services are eligible for refunds. Refund requests must be submitted within 30 days of purchase.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Billing:</span> You authorize us to charge your payment method for the services provided. You are responsible for keeping your payment information current.
            </p>
          </div>
        </section>

        {/* Service Interruption */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Zap className="w-6 h-6 text-blue-600" />
            13. Service Interruption & Maintenance
          </h2>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            We may, at our sole discretion and without notice or liability, interrupt or suspend our services for maintenance, updates, or other reasons. We will make reasonable efforts to minimize service interruptions and will provide advance notice when possible.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            We do not guarantee uninterrupted, error-free, or secure service.
          </p>
        </section>

        {/* Third-Party Services */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">14. Third-Party Services</h2>

          <p className="text-gray-700 dark:text-gray-300">
            Our website may contain links to or integrate with third-party services, websites, and applications. We are not responsible for the content, accuracy, or practices of these third-party services. Your use of third-party services is subject to their terms of service and privacy policies. We encourage you to review their terms before using their services.
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">15. Limitation of Liability</h2>

          <div className="bg-red-50 dark:bg-gray-800 p-6 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              <span className="font-semibold">Maximum Liability:</span> Our total liability to you for any claims arising from or related to these Terms of Service shall not exceed the amount paid by you to us in the 12 months preceding the claim, or $100 USD, whichever is less.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Excluded Damages:</span> In no event shall we be liable for indirect, incidental, special, consequential, punitive, or exemplary damages, regardless of the form of action and whether or not we have been advised of the possibility of such damages.
            </p>
          </div>
        </section>

        {/* Indemnification */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">16. Indemnification</h2>

          <p className="text-gray-700 dark:text-gray-300">
            You agree to indemnify, defend, and hold harmless Nevxoaulio Ventures and its officers, directors, employees, and agents from any and all claims, losses, damages, liabilities, and expenses (including attorneys' fees) arising out of or related to your use of the website, violation of these Terms of Service, or infringement of any third-party rights.
          </p>
        </section>

        {/* Severability */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">17. Severability</h2>

          <p className="text-gray-700 dark:text-gray-300">
            If any provision of these Terms of Service is found to be invalid or unenforceable, that provision shall be severed, and the remaining provisions shall remain in full force and effect.
          </p>
        </section>

        {/* Entire Agreement */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">18. Entire Agreement</h2>

          <p className="text-gray-700 dark:text-gray-300">
            These Terms of Service, together with our Privacy Policy and any other agreements you have entered into with us, constitute the entire agreement between you and us regarding your use of the website and supersede all prior and contemporaneous agreements, representations, and understandings.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-10 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-6">19. Contact Us</h2>

          <p className="text-gray-700 dark:text-gray-300 mb-6">
            If you have questions about these Terms of Service, please contact us:
          </p>

          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">Nevxoaulio Ventures</p>
              <p className="text-gray-600 dark:text-gray-300">2nd Floor, Shylashree Nilaya, 2nd cross, Banashankari, Gandinagar Tumkur, Tumkur- 572102, India</p>
            </div>

            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Email:</p>
              <a
                href="mailto:legal@nevxoaulio.com"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                legal@nevxoaulio.com
              </a>
            </div>

            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">General Inquiries:</p>
              <a
                href="mailto:info@nevxoaulio.com"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                info@nevxoaulio.com
              </a>
            </div>

            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Phone:</p>
              <a
                href="tel:+919449061503"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                +91 94490 61503
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-12">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            © {new Date().getFullYear()} Nevxoaulio Ventures. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
//
export default TermsOfService;